import { Graphics } from 'pixi.js';

// Player aura overlays — pure stateless drawing helpers.
// Caller owns the Graphics instances + the timer values + clears alive/respawn gating.

export function drawSynergyAura(g: Graphics, px: number, py: number, timer: number, alive: boolean): void {
  g.clear();
  if (timer <= 0 || !alive) return;
  const fade = Math.min(1, timer / 0.5);
  const pulse = 0.65 + 0.35 * Math.sin(Date.now() * 0.018);
  // Soft halo
  g.circle(px, py, 38);
  g.fill({ color: 0xff44ff, alpha: 0.10 * pulse * fade });
  // Main ring (thinner, breathes)
  const r = 32 + pulse * 3;
  g.circle(px, py, r);
  g.stroke({ width: 2.5, color: 0xff44ff, alpha: 0.85 * fade });
  g.circle(px, py, r - 3);
  g.stroke({ width: 1, color: 0xffaaff, alpha: 0.55 * fade });
}

// HP + shield arc rings drawn around the ship. hueIdx: 0 = player1 (cyan shield), 1 = player2 (magenta).
export function drawStatusRings(g: Graphics, sx: number, sy: number, hpFrac: number, shieldFrac: number, hueIdx: number): void {
  const pulse = 0.65 + 0.35 * Math.sin(Date.now() * 0.014);
  const hpCol = hpFrac > 0.6 ? 0x44ff88 : hpFrac > 0.3 ? 0xffcc44 : 0xff4444;
  const shCol = hueIdx === 0 ? 0x44ccff : 0xff44cc;
  const shInner = hueIdx === 0 ? 0xaaeeff : 0xffaadd;
  const R_HP = 36;
  const R_SH = 30;
  // Background rails — faint full circles so empty arcs read clearly
  g.circle(sx, sy, R_HP);
  g.stroke({ width: 2.5, color: 0x081224, alpha: 0.55 });
  g.circle(sx, sy, R_SH);
  g.stroke({ width: 2.0, color: 0x081224, alpha: 0.55 });
  const startA = -Math.PI / 2;
  if (hpFrac > 0) {
    g.moveTo(sx + Math.cos(startA) * R_HP, sy + Math.sin(startA) * R_HP);
    g.arc(sx, sy, R_HP, startA, startA + Math.PI * 2 * hpFrac);
    g.stroke({ width: 4, color: hpCol, alpha: 0.95 });
    g.moveTo(sx + Math.cos(startA) * (R_HP + 1.5), sy + Math.sin(startA) * (R_HP + 1.5));
    g.arc(sx, sy, R_HP + 1.5, startA, startA + Math.PI * 2 * hpFrac);
    g.stroke({ width: 2, color: hpCol, alpha: 0.35 });
  }
  if (shieldFrac > 0) {
    g.moveTo(sx + Math.cos(startA) * R_SH, sy + Math.sin(startA) * R_SH);
    g.arc(sx, sy, R_SH, startA, startA + Math.PI * 2 * shieldFrac);
    g.stroke({ width: 3, color: shCol, alpha: 0.85 });
    g.moveTo(sx + Math.cos(startA) * (R_SH + 1.2), sy + Math.sin(startA) * (R_SH + 1.2));
    g.arc(sx, sy, R_SH + 1.2, startA, startA + Math.PI * 2 * shieldFrac);
    g.stroke({ width: 1.5, color: shInner, alpha: 0.55 });
    // Pulsing bubble underneath while shielded
    g.circle(sx, sy, 26);
    g.fill({ color: shCol, alpha: 0.06 * pulse });
    g.circle(sx, sy, 23);
    g.stroke({ width: 1, color: shInner, alpha: 0.40 * pulse });
  }
  // Low-HP warning pulse
  if (hpFrac > 0 && hpFrac < 0.3) {
    const warn = 0.55 + 0.45 * Math.sin(Date.now() * 0.020);
    g.circle(sx, sy, R_HP + 4 + warn * 2);
    g.stroke({ width: 1.5, color: 0xff4444, alpha: 0.55 * warn });
  }
}

export function drawChargeRing(g: Graphics, px: number, py: number, chargeT: number, alive: boolean): void {
  g.clear();
  if (chargeT <= 0 || !alive) return;
  const f = Math.min(1, chargeT / 0.5);
  // Outer halo (faint)
  g.circle(px, py, 38);
  g.fill({ color: 0x00ffff, alpha: 0.05 + 0.10 * f });
  // Track ring
  g.circle(px, py, 26);
  g.stroke({ width: 2, color: 0xffaa00, alpha: 0.35 + 0.35 * f });
  // Progress arc — cyan core + soft outer arc
  g.arc(px, py, 28, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * f);
  g.stroke({ width: 5, color: 0x00ffff, alpha: 0.35 });
  g.arc(px, py, 26, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * f);
  g.stroke({ width: 3, color: 0xffffff, alpha: 0.95 });
  // Overcharged: pulsing outer + spinning tick marks
  if (chargeT >= 0.5) {
    const pulse = Math.sin(Date.now() * 0.02);
    g.circle(px, py, 32 + pulse * 4);
    g.stroke({ width: 2.5, color: 0xffffff, alpha: 0.7 });
    const ang0 = Date.now() * 0.004;
    for (let i = 0; i < 4; i++) {
      const a = ang0 + (i / 4) * Math.PI * 2;
      g.circle(px + Math.cos(a) * 36, py + Math.sin(a) * 36, 2.5);
      g.fill({ color: 0x00ffff, alpha: 0.95 });
    }
  }
}

export function drawShieldSurge(g: Graphics, px: number, py: number, timer: number, alive: boolean): void {
  g.clear();
  if (timer <= 0 || !alive) return;
  const pulse = 0.6 + 0.4 * Math.sin(Date.now() * 0.012);
  // Outer halo
  g.circle(px, py, 96);
  g.fill({ color: 0x4488ff, alpha: 0.10 * pulse });
  // Main ring
  g.circle(px, py, 90);
  g.stroke({ width: 4, color: 0x4488ff, alpha: 0.85 * pulse });
  // Inner accent
  g.circle(px, py, 84);
  g.stroke({ width: 1.5, color: 0xaaeeff, alpha: 0.55 * pulse });
  // 6 small accent dots rotating around perimeter
  const rotA = Date.now() * 0.003;
  for (let i = 0; i < 6; i++) {
    const a = rotA + (i / 6) * Math.PI * 2;
    g.circle(px + Math.cos(a) * 90, py + Math.sin(a) * 90, 3);
    g.fill({ color: 0xaaeeff, alpha: 0.95 });
  }
}
